import {
  ApiAccessChartWidget,
  Box,
  ApiChartConfig,
  DropdownOptions,
  Flex,
  Spinner,
  Text,
} from "dash-ui";
import { ApiCredentialsTypesEnum } from "dash-common";
import { useGetApiCredentialsByIdQuery } from "gql/generated.graphql";
import { matchPath, useLocation, useNavigate } from "react-router-dom";
import { RoutesPath } from "flow-components/routes/routes";
import { useEffect, useMemo, useState } from "react";

import { totalApiUsageFetchFunction } from "../helpers/helpers";

const responsiveBoxWidth: [string, string, string, string] = [
  "100%",
  "100%",
  "100%",
  "calc(50% - 8px)",
];

const initialCharts: ApiChartConfig[] = [
  {
    title: "Total API Access",
    fetchFunction: totalApiUsageFetchFunction({}),
    filters: { appId: null, getCompanyTotal: true },
  },
  {
    title: "Company Direct API Access",
    fetchFunction: totalApiUsageFetchFunction({}),
    filters: { type: ApiCredentialsTypesEnum.COMPANY, appId: null },
  },
  {
    title: "Clients API Access",
    fetchFunction: totalApiUsageFetchFunction({}),
    filters: {
      type: ApiCredentialsTypesEnum.PLUGIN_USER,
      appId: null,
      getCompanyClients: true,
    },
  },
];

const ApiCompanyDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get api credentials from the current url
  const pathname = location.pathname;
  const propertiesParams = matchPath(
    RoutesPath.APIManagementCompanyDetails,
    pathname
  );
  const apiCredentialsId = propertiesParams?.params?.id;

  const [companyName, setCompanyName] = useState("");

  const { data, loading, error } = useGetApiCredentialsByIdQuery({
    variables: apiCredentialsId ? { apiCredentialsId } : undefined,
    skip: !apiCredentialsId,
  });

  // Compute charts with the correct appId once data is loaded
  const charts = useMemo<ApiChartConfig[]>(() => {
    const appId = data?.getApiCredentialsById?.appId ?? null;
    return initialCharts.map((chart) => ({
      ...chart,
      fetchFunction: totalApiUsageFetchFunction(
        chart.filters ? { ...chart.filters, appId } : {}
      ),
      filters: chart.filters ? { ...chart.filters, appId } : undefined,
    }));
  }, [data]);

  useEffect(() => {
    if (data?.getApiCredentialsById?.companyName) {
      setCompanyName(data.getApiCredentialsById.companyName);
    }
  }, [data]);

  return (
    <Box w="100%" p={6}>
      <DropdownOptions />
      {loading ? (
        <Flex w="100%" alignItems="center" justifyContent="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Text color="red">Failed to load Company information.</Text>
      ) : (
        <>
          <Flex mb={8}>
            <Text xl bold>
              {companyName}
            </Text>
          </Flex>
          <Flex gap={16} flexWrap="wrap">
            {charts.map(({ title, fetchFunction }) => (
              <Box w={responsiveBoxWidth} key={title}>
                <ApiAccessChartWidget
                  title={title}
                  fetchFunction={fetchFunction}
                />
              </Box>
            ))}
          </Flex>
          <Flex mt={16}>
            <Box
              as="button"
              type="button"
              className="cursor-pointer"
              style={{ background: "none", border: "none", padding: 0 }}
              onClick={() =>
                navigate(`${RoutesPath.APIManagement}?tab=PLUGIN_USERS`, {
                  state: { companyName },
                })
              }
            >
              <Text textDecoration="underline">View company's clients</Text>
            </Box>
          </Flex>
        </>
      )}
    </Box>
  );
};

export default ApiCompanyDetailsPage;
